<template>
  <div>
    <div class="fixed bottom-0 left-1/2 transform -translate-x-1/2 max-w-[375px] w-full z-10 filseyle all:box-border">

      <div class="flex items-center justify-between">
        <div>{{ t('relatorios.accountDetails.totalDeposit') }}:</div>
        <div class="color-[#21CB6E]"> {{ rechargeAmount }}</div>
      </div>
      <div class="flex items-center justify-between my-[10px]">
        <div>{{ t('relatorios.accountDetails.totalWithdraw') }}:</div>
        <div class="color-[#FC493D]"> {{ withdrawAmount }}</div>
      </div>
      <div class="flex items-center justify-between">
        <div>{{ t('relatorios.accountDetails.totalReward') }}:</div>
        <div class="color-[#FCEE40]"> {{ rewardAmount }}</div>
      </div>
    </div>
    <div class="bonus-table-header">
      <Border class="!w-1/4" radius="4px" borderWidth="2px">
        <div class="date-btn w-full" :class="{ active: activeDropdown === 1 }" @click="toggleDropdown(1)">
          <span class="btn-text">{{ dateOptions[selectedDate1].label }}</span>
          <span :class="['arrow', activeDropdown === 1 ? 'up' : 'down']"></span>
        </div>
      </Border>
      <Border class="!w-1/4" v-if="!IS_DOUBLE_WALLET" radius="4px" borderWidth="2px">
        <div class="date-btn w-full" :class="{ active: activeDropdown === 4 }" @click="toggleDropdown(4)">
          <span class="btn-text">{{ walletTypeOptions[selectedDate4].label }}</span>
          <span :class="['arrow', activeDropdown === 4 ? 'up' : 'down']"></span>
        </div>
      </Border>
      <Border class="!w-1/4" radius="4px" borderWidth="2px">
        <div class="date-btn w-full" :class="{ active: activeDropdown === 2 }" @click="toggleDropdown(2)">
          <span class="btn-text">{{ typeOptions[selectedType].label }}</span>
          <span :class="['arrow', activeDropdown === 2 ? 'up' : 'down']"></span>
        </div>
      </Border>
      <Border class="!w-1/4" radius="4px" borderWidth="2px">
        <div class="date-btn w-full" :class="{ active: activeDropdown === 3 }" @click="toggleDropdown(3)">
          <span class="btn-text">{{ filses[selectedDate3].label }}</span>
          <span :class="['arrow', activeDropdown === 3 ? 'up' : 'down']"></span>
        </div>
      </Border>
    </div>

    <!-- 下拉内容区域，独立块级展示 -->
    <div v-if="activeDropdown === 1" class="dropdown-panel">
      <button v-for="(item, idx) in dateOptions" :key="item.value"
        :class="['option-btn', { active: idx === selectedDate1 }]" @click="selectDate(idx, 1)">
        {{ item.label }}
      </button>
    </div>
    <div v-if="activeDropdown === 2" class="dropdown-panel grid-panel">
      <button v-for="(item, idx) in typeOptions" :key="item.value"
        :class="['option-btn', { active: idx === selectedType }]" @click="selectType(idx)">
        {{ item.label }}
      </button>
    </div>
    <div v-if="activeDropdown === 3" class="dropdown-panel">
      <button v-for="(item, idx) in filses" :key="item.value" :class="['option-btn', { active: idx === selectedDate3 }]"
        @click="selectDate(idx, 3)">
        {{ item.label }}
      </button>
    </div>
    <div v-if="activeDropdown === 4" class="dropdown-panel">
      <button v-for="(item, idx) in walletTypeOptions" :key="item.value"
        :class="['option-btn', { active: idx === selectedDate4 }]" @click="selectDate(idx, 4)">
        {{ item.label }}
      </button>
    </div>
    <div v-if="datalist.length === 0 && !loading" class="empty-data">
      <div class="flex justify-center items-center">
        <img :src="getAssetsFile('home/No_record@2x.png')" alt="" class="max-h-[84px] max-w-[106.5px] w-auto h-auto">
      </div>
      <div class="empty-text">{{ t('relatorios.accountDetails.noRecords') }}</div>
    </div>

    <div v-else class="record-list">
      <div class="record-item" v-for="item in datalist" :key="item.id">
        <div class="record-header">
          <span class="record-title">{{ getTypeText(item.type) }}</span>
          <div class="flex">
            <span class="text-white text-[13px]">
              {{ item.currencyId == 3 ? 'Bonus' : 'Cash' }}
            </span>
            <span class="record-amount" :class="{ positive: item.amount > 0, negative: item.amount < 0 }">
              {{ item.amount > 0 ? '+' : '' }}{{ item.amount }}
            </span>
          </div>

        </div>
        <div class="record-footer">
          <span class="record-time color-[#49AC90]">{{ formatTime(item.time) }}</span>
          <span class="record-id">
            {{ item.orderId }}
            <img :src="getAssetsFile('home/copy.png')" alt="" class="icon-copy" @click="copy(item.orderId)"
              title="copy">
          </span>
        </div>
      </div>

      <!-- 加载更多提示 -->
      <div v-if="loading" class="loading-more">
        <div class="loading-spinner"></div>
        <span>{{ t('relatorios.loading') }}</span>
      </div>

      <!-- 没有更多数据提示 -->
      <div v-if="!hasMore && datalist.length > 0" class="no-more">
        {{ t('relatorios.accountDetails.noMoreData') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Border } from "@/components/Border"
import { ref, onMounted, onUnmounted } from 'vue'
import * as GameApi from '@/api/game'
import dayjs from 'dayjs'
import { useClipboard } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { getAssetsFile } from '@/utils/pub-use'
import { getStartOfToday, getStartOfDaysAgo, formatDateTime } from '@/utils/date'
const IS_DOUBLE_WALLET = import.meta.env?.VITE_WALLET_SWITCH == 'true' ? true : false;
const { t } = useI18n()

// 获取当天0点
function getStartOfDay(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
}

// 格式化为 yyyy-MM-dd 00:00:00
function formatDate(date: Date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d} 00:00:00`
}

const now = new Date()
const today = getStartOfDay(now)

const dateOptions = [
  { label: t('relatorios.accountDetails.today'), value: formatDate(today) },
  { label: t('relatorios.accountDetails.last3Days'), value: formatDate(new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000)) },
  { label: t('relatorios.accountDetails.last5Days'), value: formatDate(new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000)) },
  { label: t('relatorios.accountDetails.last7Days'), value: formatDate(new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000)) },
]

const typeOptions = [
  { label: t('relatorios.accountDetails.allTypes'), value: '' },
  { label: t('relatorios.accountDetails.memberDeposit'), value: 1 },
  { label: t('relatorios.accountDetails.withdraw'), value: 2 },
  { label: t('relatorios.accountDetails.events'), value: 3 },
  { label: t('relatorios.accountDetails.commission'), value: 4 },
]

const filses = [
  { label: t('relatorios.accountDetails.allDetails'), value: 'all' },
]

const walletTypeOptions = [
  { label: t('relatorios.accountDetails.totalWallet'), value: null },
  { label: t('relatorios.accountDetails.cash'), value: 1 },
  { label: t('relatorios.accountDetails.bonus'), value: 2 },
]
// 响应式数据 - 这些被意外删除了，需要重新添加
const selectedDate1 = ref(0)
const selectedDate3 = ref(0)
const selectedType = ref(0)
const selectedDate4 = ref(0)
const activeDropdown = ref<number | null>(null)
const datalist = ref([])
const total = ref(0)
const loading = ref(false)
const hasMore = ref(true)

const params = ref({
  pageNo: 1,
  pageSize: 16,
  time: dateOptions[0].value,
  total: null,
  type: null,
  currencyType: null,
})

// 多语言函数
function getTypeText(type: number) {
  return t(`relatorios.accountDetails.transactionTypes.${type}`) || type.toString()
}

const { copy: copyToClipboard } = useClipboard()

const copy = (text: string) => {
  copyToClipboard(text)
    ; (window as any).$toast(t('relatorios.accountDetails.copySuccess'))
}

function toggleDropdown(idx: number) {
  activeDropdown.value = activeDropdown.value === idx ? null : idx
}

function selectDate(idx: number, dropdownIdx: number) {
  // 钱包类型(等待后端接口完成)
  if (dropdownIdx === 4) {
    selectedDate4.value = idx
    params.value.currencyType = walletTypeOptions[idx].value
    resetAndFetch()
  }


  if (dropdownIdx === 1) {
    selectedDate1.value = idx
    params.value.time = formatDateTime(dateOptions[idx].value)
    resetAndFetch()
  }
  if (dropdownIdx === 3) selectedDate3.value = idx
  activeDropdown.value = null
}

function selectType(idx: number) {
  selectedType.value = idx
  params.value.type = typeOptions[idx].value
  activeDropdown.value = null
  resetAndFetch()
}

// 重置并重新获取数据
function resetAndFetch() {
  params.value.pageNo = 1
  datalist.value = []
  hasMore.value = true
  fetchData()
}

const rechargeAmount = ref(0)
const rewardAmount = ref(0)
const withdrawAmount = ref(0)
const cashAmount = ref(0)
const bonusAmount = ref(0)
// 获取数据
function fetchData() {
  if (loading.value || !hasMore.value) return

  loading.value = true
  params.value.time = formatDateTime(params.value.time)

  GameApi.accountFlow(params.value).then(res => {
    console.log(res, "AccountDetails")
    rechargeAmount.value = res.data.rechargeAmount
    rewardAmount.value = res.data.rewardAmount
    withdrawAmount.value = res.data.withdrawAmount
    bonusAmount.value = res.data.bound;
    cashAmount.value = res.data.cash;
    const newData = res.data.assetRecords.list
    total.value = res.data.assetRecords.total

    if (params.value.pageNo === 1) {
      // 第一页，直接替换数据
      datalist.value = newData
    } else {
      // 后续页面，合并数据
      datalist.value = [...datalist.value, ...newData]
    }

    // 判断是否还有更多数据
    hasMore.value = datalist.value.length < total.value
    loading.value = false
  }).catch(() => {
    loading.value = false
  })
}

// 加载下一页
function loadMore() {
  if (loading.value || !hasMore.value) return

  params.value.pageNo++
  fetchData()
}

// 滚动监听
function handleScroll() {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const windowHeight = window.innerHeight
  const documentHeight = document.documentElement.scrollHeight

  // 距离底部100px时触发加载
  if (scrollTop + windowHeight >= documentHeight - 100) {
    loadMore()
  }
}

function formatTime(val: number | string) {
  return formatDateTime(Number(val))
}

onMounted(() => {
  fetchData()
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.filseyle {
  background: #006849;
  height: auto;
  min-height: 80px;
  padding: 18px 14px;
  font-size: 12px;
  color: #fff;
}

.bonus-table-header {
  margin: 15px 15px 0 15px;
  display: flex;
  align-items: center;
  position: relative;
  gap: 6px;
  /* 减少按钮间距 */
}

.date-btn {
  background: transparent;
  /* border: 1px solid #40b665; */
  color: #fff;
  border-radius: 8px;
  padding: 4px 8px;
  /* 减少左右内边距 */
  font-size: 10px;
  /* 减小字体 */
  font-weight: 500;
  outline: none;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  /* 让按钮平均分配宽度 */
  /* min-width: 25%; */
  /* 允许按钮收缩 */
  /* max-width: calc(33.333% - 4px); */
  /* 限制最大宽度 */
}

.btn-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.arrow {
  display: inline-block;
  margin-left: 4px;
  /* 减少箭头左边距 */
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  /* 减小箭头大小 */
  border-right: 4px solid transparent;
  border-top: 4px solid #40b665;
  transition: transform 0.2s;
  flex-shrink: 0;
  /* 防止箭头被压缩 */
}

.arrow.up {
  transform: rotate(180deg);
}

.arrow.down {
  transform: rotate(0deg);
}

.dropdown-panel {
  width: 100%;
  background: #02523d;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 8px;
  margin-bottom: 10px;
}

.grid-panel {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 10px 8px;
}

.option-btn {
  background: transparent;
  border: 1px solid #40b665;
  color: #40b665;
  border-radius: 8px;
  padding: 5px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  /* transition: background 0.2s, color 0.2s; */
  min-width: 50px;
  text-align: center;
  word-break: break-all;
  min-height: 40px;
}

.option-btn.active,
.option-btn:hover {
  border: 1px solid #40b665;
  background: linear-gradient(90deg, #40b665 0%, #1ecb7b 100%);
  color: #fff;

}

.empty-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 115px;
}

.empty-text {
  color: #ADB1B0;
  font-size: 11px;
  margin-top: 21px;
}

.record-list {
  padding: 0 10px;
  margin-top: 15px;
  margin-bottom: 120px;
  /* 增加底部边距，给固定元素留出更多空间 */
}

.record-item {
  background: #085b3c;
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 12px 14px 10px 14px;
  color: #fff;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
}

.record-title {
  font-weight: 500;
  font-size: 12px;
}

.record-amount {
  font-weight: bold;
  font-size: 13px;
}

.record-amount.positive {
  color: #40b665;
}

.record-amount.negative {
  color: #ff4d4f;
}

.record-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
  font-size: 13px;
  color: #b2e0c6;
}

.record-id {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #49AC90;
}

.icon-copy {
  display: inline-block;
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin-left: 4px;
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #60B289;
  font-size: 14px;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #40b665;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.no-more {
  text-align: center;
  padding: 20px;
  color: #60B289;
  font-size: 14px;
}
</style>
